import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Video {
  id: string;
  title: string;
  description: string | null;
  video_type: "youtube" | "upload";
  youtube_id: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
}

/**
 * Lite YouTube facade: renders the thumbnail only; loads the iframe (and
 * third-party cookies/JS) just after the user clicks play. Massively reduces
 * main-thread work and third-party requests on initial load.
 */
const YouTubeFacade = ({ id, title }: { id: string; title: string }) => {
  const [active, setActive] = useState(false);

  if (active) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActive(true)}
      aria-label={`Reproducir ${title}`}
      className="relative w-full h-full group"
    >
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt={title}
        loading="lazy"
        decoding="async"
        width={480}
        height={360}
        className="w-full h-full object-cover"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
        <span className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center shadow-lg">
          <Play size={28} className="text-primary-foreground ml-1" />
        </span>
      </span>
    </button>
  );
};

const VideoSection = () => {
  const [videos, setVideos] = useState<Video[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("videos")
        .select("id, title, description, video_type, youtube_id, video_url, thumbnail_url")
        .eq("published", true)
        .order("display_order", { ascending: true });
      setVideos((data as Video[]) || []);
    })();
  }, []);

  if (videos.length === 0) return null;

  return (
    <section id="videos" className="py-24 section-gradient">
      <div className="container max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-sm font-semibold tracking-wider uppercase text-primary mb-3 block">
            Videos
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
            Conoce Nuestras Soluciones en Acción
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {videos.map((video, i) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="glass rounded-2xl overflow-hidden"
            >
              <div className="aspect-video bg-black">
                {video.video_type === "youtube" && video.youtube_id ? (
                  <YouTubeFacade id={video.youtube_id} title={video.title} />
                ) : video.video_url ? (
                  <video
                    src={video.video_url}
                    poster={video.thumbnail_url || undefined}
                    controls
                    preload="none"
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>
              <div className="px-5 py-4">
                <h3 className="font-semibold text-foreground">{video.title}</h3>
                {video.description && (
                  <p className="text-sm text-muted-foreground mt-1">{video.description}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
